import React, { useState, useEffect } from 'react';
import { Swords, Gem } from 'lucide-react';
import BuyResult from './BuyResult';

const API = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

const WeaponsArsenal = ({ gameId, eraNum, onRefresh }) => {
  const [weapons, setWeapons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(null);
  const [buyResult, setBuyResult] = useState(null);

  useEffect(() => { loadWeapons(); }, [gameId, eraNum]);

  const loadWeapons = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/store/weapons/${gameId}`);
      if (res.ok) {
        const data = await res.json();
        setWeapons(data.eras?.[eraNum] || []);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const handleBuy = async (weaponId) => {
    setBuying(weaponId);
    try {
      const res = await fetch(`${API}/api/store/weapons/buy`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ game_id: gameId, weapon_id: weaponId }),
      });
      if (res.ok) {
        const data = await res.json();
        setBuyResult(data);
        setTimeout(() => setBuyResult(null), 3000);
        if (data.success) { onRefresh(); loadWeapons(); }
      }
    } catch (e) { console.error(e); }
    setBuying(null);
  };

  if (loading) return <p className="text-[#E8E0D0]/20 text-xs animate-pulse p-8 text-center">Loading arsenal...</p>;

  return (
    <div data-testid="weapons-arsenal">
      <div className="flex items-center gap-2 mb-1">
        <Swords size={16} className="text-[#8B1A1A]" />
        <h3 className="font-['Cormorant_Garamond'] text-[#8B1A1A] text-lg font-bold">ERA {eraNum} ARSENAL</h3>
      </div>
      <p className="font-['Outfit'] text-[#E8E0D0]/25 text-[9px] tracking-widest mb-4">
        {eraNum === 1 ? 'STREET WEAPONS — 1965-1979' : eraNum === 2 ? 'BLOCK ARSENAL — 1979-1992' : eraNum === 3 ? 'EMPIRE FIREPOWER — 1992-2005' : 'MODERN WARFARE — 2005-2026'}
      </p>
      <BuyResult result={buyResult} />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {weapons.map(w => (
          <div key={w.id} className={`bg-[#111820] border rounded-lg p-3 flex items-center justify-between gap-3 ${
            w.locked ? 'border-[#1A2535] opacity-40' : w.legacy ? 'border-[#4ADE80]/15' : 'border-[#8B1A1A]/20 hover:border-[#8B1A1A]/40'
          } transition-all`} data-testid={`weapon-${w.id}`}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h5 className="font-['Outfit'] text-[#E8E0D0] text-xs font-semibold">{w.name}</h5>
                {w.locked && <span className="font-['Outfit'] text-[7px] tracking-widest px-1 py-0.5 bg-[#8B1A1A]/15 text-[#8B1A1A] rounded border border-[#8B1A1A]/20">LOCKED</span>}
                {w.legacy && <span className="font-['Outfit'] text-[7px] tracking-widest px-1 py-0.5 bg-[#4ADE80]/10 text-[#4ADE80] rounded border border-[#4ADE80]/15">LEGACY</span>}
              </div>
              <p className="font-['Outfit'] text-[#8B1A1A]/60 text-[10px] mt-0.5">{w.effect}</p>
              {w.attack_boost > 0 && (
                <div className="flex items-center gap-1 mt-1">
                  <Swords size={9} className="text-[#8B1A1A]/50" />
                  <span className="font-['Outfit'] text-[#8B1A1A]/50 text-[9px]">+{w.attack_boost} ATK</span>
                </div>
              )}
              {w.matt_line && <p className="font-['Cormorant_Garamond'] text-[#E8E0D0]/15 italic text-[10px] mt-1">"{w.matt_line}"</p>}
            </div>
            <button onClick={() => handleBuy(w.id)} disabled={buying === w.id || w.locked || !w.can_afford}
              className="px-2.5 py-1.5 border rounded font-['Outfit'] text-[9px] tracking-wider transition-all shrink-0 disabled:opacity-25 disabled:cursor-not-allowed bg-[#8B1A1A]/10 border-[#8B1A1A]/30 text-[#8B1A1A] hover:bg-[#8B1A1A]/20"
              data-testid={`buy-weapon-${w.id}`}>
              <span className="flex items-center gap-1"><Gem size={9} />{w.crystal_cost?.toLocaleString()}</span>
            </button>
          </div>
        ))}
      </div>
      {weapons.length === 0 && <p className="text-center text-[#E8E0D0]/20 text-xs py-8">No weapons available for this era.</p>}
    </div>
  );
};

export default WeaponsArsenal;
